import {
    Box,
    Button,
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Theme,
} from '@mui/material';
import {DateTime} from 'luxon';
import {DesktopDatePicker} from '@mui/x-date-pickers/DesktopDatePicker';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {useDispatch, useSelector} from 'react-redux';
import {Selector} from '../../../state/selector';
import {User} from '../../../state/types/User';
import {useEffect} from 'react';
import Command from '../../../state/actions/command';

interface WorkingTimeFilterBarProps {
    start: DateTime | null;
    end: DateTime | null;
    onChangeStart: (newValue: DateTime | null) => void;
    onChangeEnd: (newValue: DateTime | null) => void;
    onClickFilterList: () => void;
    onClickResetFilter: () => void;
    filterIsSet: boolean;
    selectedEmployeeIds: string[];
    onChangeEmployeeIds: (ids: string[]) => void;
    projectEmployees: string[];
}

const styles = {
    startEndWrapper: (theme: Theme) => ({
        marginTop: 1,
        marginBottom: 5,
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            alignItems: 'center',
        },
    }),
    endWrapper: (theme: Theme) => ({
        [theme.breakpoints.down('md')]: {
            marginTop: 1,
        },
        [theme.breakpoints.up('md')]: {
            marginLeft: 1,
        },
    }),
};

const WorkingTimeFilterBar = (props: WorkingTimeFilterBarProps) => {
    const dispatch = useDispatch();
    const users = useSelector(Selector.User.users);

    useEffect(() => {
        props.projectEmployees.forEach((employeeId) => {
            if (!users[employeeId]) {
                dispatch(Command.User.fetchUser({ userId: employeeId }));
            }
        });
    }, [props.projectEmployees]);

    const getEmployeeName = (employeeId: string): string => {
        const user: User | undefined = users[employeeId];
        return user ? `${user.firstName} ${user.lastName}` : employeeId;
    };

    return (
        <Box sx={styles.startEndWrapper}>
            <Box>
                <DesktopDatePicker
                    label={'Start'}
                    value={props.start}
                    onChange={props.onChangeStart}
                    renderInput={(params) => <TextField {...params} />}
                    maxDate={DateTime.now()}
                    inputFormat={'dd.MM.yyyy'}
                    mask={'__/__/____'}
                    views={['day']}
                />
            </Box>
            <Box sx={styles.endWrapper}>
                <DesktopDatePicker
                    label={'Ende'}
                    value={props.end}
                    onChange={props.onChangeEnd}
                    renderInput={(params) => <TextField {...params} />}
                    maxDate={DateTime.now()}
                    minDate={props.start!}
                    inputFormat={'dd.MM.yyyy'}
                    mask={'__/__/____'}
                    views={['day']}
                />
            </Box>
            <Box sx={styles.endWrapper}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id={'employee-filter-label'}>Mitarbeiter</InputLabel>
                    <Select
                        labelId={'employee-filter-label'}
                        multiple={true}
                        value={props.selectedEmployeeIds}
                        onChange={(e) => {
                            const value = e.target.value;
                            props.onChangeEmployeeIds(
                                typeof value === 'string' ? value.split(',') : value,
                            );
                        }}
                        renderValue={(selected) => selected.map(getEmployeeName).join(', ')}
                        label={'Mitarbeiter'}
                    >
                        {props.projectEmployees.map((employeeId) => (
                            <MenuItem key={employeeId} value={employeeId}>
                                <Checkbox checked={props.selectedEmployeeIds.indexOf(employeeId) > -1} />
                                <ListItemText primary={getEmployeeName(employeeId)} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            &nbsp;&nbsp;&nbsp;
            <Button
                variant={'contained'}
                startIcon={<FilterListIcon />}
                children={'Filtern'}
                onClick={props.onClickFilterList}
            />
            &nbsp;&nbsp;&nbsp;
            <Button
                variant={'contained'}
                startIcon={<RestartAltIcon />}
                children={'Zurücksetzen'}
                onClick={props.onClickResetFilter}
                disabled={!props.filterIsSet}
            />
        </Box>
    );
};

export default WorkingTimeFilterBar;
